import * as React from 'react'
import {
	TextInput as RNTextInput,
	View,
	StyleSheet,
	TextStyle,
	TextInputProps as RNTextInputProps,
} from 'react-native'
import { Text } from './Text'
import { Stack } from './Stack'
import { colors } from './theme'
import { spacing } from './types'
import { Controller, Control, FieldValues, Path } from 'react-hook-form'
import { Platform } from 'react-native'

type Size = 'standard' | 'small' | 'large'
type Width = 'full' | 'large' | 'medium' | 'small'

interface TextInputProps<T extends FieldValues> extends Omit<RNTextInputProps, 'style'> {
	label?: string
	description?: string
	error?: string
	size?: Size
	width?: Width
	required?: boolean
	tone?: 'neutral' | 'critical' | 'positive'
	style?: TextStyle
	prefix?: string
	maxNumber?: number
	// React Hook Form props
	control?: Control<T>
	name?: Path<T>
	ref?: React.ForwardedRef<RNTextInput>
}

const widthMap = {
	full: '100%',
	large: 500,
	medium: 400,
	small: 300,
} as const

function BaseTextInput(
	{
		label,
		description,
		error,
		size = 'standard',
		width = 'full',
		required = false,
		tone = 'neutral',
		style,
		prefix,
		maxNumber,
		onChangeText,
		...props
	}: Omit<TextInputProps<any>, 'control' | 'name'>,
	ref: React.ForwardedRef<RNTextInput>
) {
	const fontSize = size === 'standard' || size === 'large' ? 16 : 14
	const placeholderFontSize = size === 'standard' ? 14 : size === 'large' ? 16 : 12
	const horizontalPadding = size === 'standard' ? spacing.sm : spacing.xs
	const verticalPadding = size === 'standard' ? 12 : size === 'large' ? 18 : 8

	const borderColor = error
		? colors.red500
		: tone === 'positive'
			? colors.green500
			: tone === 'critical'
				? colors.red500
				: colors.gray500

	const handleChangeText = (text: string) => {
		if (maxNumber !== undefined && onChangeText && props.keyboardType === 'numeric') {
			const num = parseFloat(text)
			if (!isNaN(num) && num <= maxNumber) {
				onChangeText(text)
			} else if (text === '') {
				onChangeText(text)
			}
		} else if (onChangeText) {
			onChangeText(text)
		}
	}

	return (
		<View style={[styles.container, { width: widthMap[width] }]}>
			<Stack gap="xs">
				{label && (
					<Text level={size === 'standard' ? 'body' : 'small'} weight="medium">
						{label}
						{required && (
							<Text tone="critical" weight="medium">
								*
							</Text>
						)}
					</Text>
				)}
				{description && (
					<Text level="small" tone="secondary">
						{description}
					</Text>
				)}
				<View style={styles.inputContainer}>
					{prefix && (
						<Text
							style={[
								styles.prefix,
								{
									fontSize: props.value ? fontSize : placeholderFontSize,
								},
							]}
						>
							{prefix}
						</Text>
					)}
					<RNTextInput
						ref={ref}
						style={[
							styles.input,
							{
								fontSize: props.value ? fontSize : placeholderFontSize,
								paddingHorizontal: horizontalPadding,
								paddingVertical: verticalPadding,
								borderColor,
								paddingLeft: prefix ? 32 : horizontalPadding,
							},
							style,
						]}
						placeholderTextColor={colors.gray400}
						onChangeText={handleChangeText}
						{...props}
					/>
				</View>
				{error && (
					<Text level="small" tone="critical">
						{error}
					</Text>
				)}
			</Stack>
		</View>
	)
}

export const ForwardedTextInput = React.forwardRef(BaseTextInput)

export function TextInput<T extends FieldValues>({
	control,
	name,
	error: propError,
	...props
}: TextInputProps<T>) {
	if (control && name) {
		return (
			<Controller
				control={control}
				name={name}
				render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
					<ForwardedTextInput
						{...props}
						ref={ref}
						value={value}
						onChangeText={onChange}
						onBlur={onBlur}
						error={error?.message || propError}
					/>
				)}
			/>
		)
	}

	return <ForwardedTextInput error={propError} {...props} />
}

const styles = StyleSheet.create({
	container: {
		width: '100%',
	},
	inputContainer: {
		flexDirection: 'row',
		alignItems: 'center',
	},
	input: {
		width: '100%',
		padding: 16,
		borderWidth: 1,
		backgroundColor: '#fcfcfc',
		borderRadius: 16,
		fontFamily: Platform.OS === 'web' ? 'System' : 'DefaultFont',
	},
	prefix: {
		position: 'absolute',
		left: 16,
		zIndex: 1,
		color: colors.gray900,
		paddingRight: 8,
	},
})
